import type { IMetadata } from '@adloid-technologies/discover-sdk';

/**
 * Cadillac Optiq (linkId 2366, project 59).
 *
 * Converted from the 1.0 repo's `src/metadata/data/metadata-2366.ts`
 * (`appData.visualiser.models[0]`, array-shaped) into the 2.0 object-keyed
 * `IMetadata` shape, following the conventions established for 2382/2377:
 * consolidate-or-split fingerprinting per entity, `autoIntelRPOCode` from
 * `brandItemCode`, real condition/isDefault/isStop threading on accessory
 * messages, model-level `hotspots` built from the default variant, and
 * `groupId`/`isDefault` derived from `removeAccessories` via connected
 * components (with single-select group categories normalised to the group's
 * dominant category — 1.0 sometimes mislabels one member).
 *
 * PARTIAL ASSET GAP, same root cause as Yukon/Lyriq: probed all 112 unique
 * 1.0 image references individually against the deployed host. The
 * legacy '2025 Luxury' trim's 11 exterior colours, 4 interior colours and
 * 10 feature stills (25 refs) don't resolve under any extension — every
 * OTHER reference, including every 2025 Luxury ACCESSORY and all three
 * 2026 trims in full, resolves cleanly. Missing refs left as empty
 * strings (MISSING_2025_LUXURY set above), not fabricated paths.
 * 
 * 2026 OPTIQ-V (the default variant) carries this project's only two
 * packages — Santorini Blue Package and Carbon Fiber Package — each
 * toggling a single accessory.
 *
 * Generated — see the conversion notes in ui-metadata.ts.
 */
export const metadata: IMetadata = {
  models: {
    Optiq: {
      id: 'Optiq',
      lightingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      postProcessingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      backgroundIdentifer: 'dome',
      variants: {
        '2025-luxury': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5018',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'common_interior',
              sku: '5020',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5008',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5021',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '4256',
              projectId: '49',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        '2026-optiq-v': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6235',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'common_interior',
              sku: '6238',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6239',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6232',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '4256',
              projectId: '49',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        '2026-premium-luxury': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6235',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'common_interior',
              sku: '6238',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6239',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6371',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '4256',
              projectId: '49',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        '2026-luxury': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6235',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'common_interior',
              sku: '6238',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6239',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6331',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '4256',
              projectId: '49',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          isDefault: true,
          sku: '4256',
          project: '49',
        },
        g0ZA968_RpUe: {
          isDefault: false,
          sku: '4850',
          project: '59',
        },
      },
      ambientLighting: {
        ambientColors: '#0040FF,#ff1300,#ff9f51,#aafef4,#00b6ff,#61ff87',
        ambientLightings: {
          ambientBlue: {
            isDefault: true,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2026-optiq-v': 'common_interior',
              '2026-premium-luxury': 'common_interior',
              '2026-luxury': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW_1',
              'INT_AMBIENT_GLOW_2',
              'INT_AMBIENT_GLOW_3',
              'INT_AMBIENT_GLOW_4',
            ],
            parentNodeName: [],
            emissiveColor: [0, 64, 255],
            emissiveIntensity: 0.5,
          },
          ambientRed: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2026-optiq-v': 'common_interior',
              '2026-premium-luxury': 'common_interior',
              '2026-luxury': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW_1',
              'INT_AMBIENT_GLOW_2',
              'INT_AMBIENT_GLOW_3',
              'INT_AMBIENT_GLOW_4',
            ],
            parentNodeName: [],
            emissiveColor: [255, 19, 0],
            emissiveIntensity: 0.5,
          },
          ambientOrange: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2026-optiq-v': 'common_interior',
              '2026-premium-luxury': 'common_interior',
              '2026-luxury': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW_1',
              'INT_AMBIENT_GLOW_2',
              'INT_AMBIENT_GLOW_3',
              'INT_AMBIENT_GLOW_4',
            ],
            parentNodeName: [],
            emissiveColor: [255, 159, 81],
            emissiveIntensity: 0.5,
          },
          ambientCyan: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2026-optiq-v': 'common_interior',
              '2026-premium-luxury': 'common_interior',
              '2026-luxury': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW_1',
              'INT_AMBIENT_GLOW_2',
              'INT_AMBIENT_GLOW_3',
              'INT_AMBIENT_GLOW_4',
            ],
            parentNodeName: [],
            emissiveColor: [170, 254, 244],
            emissiveIntensity: 0.5,
          },
          ambientSkyBlue: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2026-optiq-v': 'common_interior',
              '2026-premium-luxury': 'common_interior',
              '2026-luxury': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW_1',
              'INT_AMBIENT_GLOW_2',
              'INT_AMBIENT_GLOW_3',
              'INT_AMBIENT_GLOW_4',
            ],
            parentNodeName: [],
            emissiveColor: [0, 182, 255],
            emissiveIntensity: 0.5,
          },
          ambientGreen: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              '2025-luxury': 'common_interior',
              '2026-optiq-v': 'common_interior',
              '2026-premium-luxury': 'common_interior',
              '2026-luxury': 'common_interior',
            },
            materialName: [
              'INT_AMBIENT_GLOW_1',
              'INT_AMBIENT_GLOW_2',
              'INT_AMBIENT_GLOW_3',
              'INT_AMBIENT_GLOW_4',
            ],
            parentNodeName: [],
            emissiveColor: [97, 255, 135],
            emissiveIntensity: 0.5,
          },
        },
        relevantVariants: ['2025-luxury', '2026-optiq-v', '2026-premium-luxury', '2026-luxury'],
      },
      colors: {
        exterior: {
          '10jb3cwz3ub7': {
            sku: '5008',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          z3686mwtpyj7: {
            sku: '5009',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '0zrjd3lm53he': {
            sku: '5010',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          cq03qyp7gnby: {
            sku: '5013',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '10xy9i682nxc': {
            sku: '5011',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '37xytt0490j8': {
            sku: '5012',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '536y2x49kxgq': {
            sku: '5014',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '4zd4onnmg2y5': {
            sku: '5015',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '2phvl5sq89cx': {
            sku: '5016',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '03495u2095u209': {
            sku: '5017',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '2phv23423452l5sq89cx': {
            sku: '5019',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
          },
          '9951ydicoxbo': {
            sku: '6239',
            project: '59',
            relevantVariants: [
              '2026-optiq-v',
            ],
          },
          dhj6dscvmgab: {
            sku: '6231',
            project: '59',
            relevantVariants: [
              '2026-optiq-v',
            ],
          },
          pm2x0dyqhgt7: {
            sku: '6229',
            project: '59',
            relevantVariants: [
              '2026-optiq-v',
            ],
          },
          mxstdl6cglti: {
            sku: '6230',
            project: '59',
            relevantVariants: [
              '2026-optiq-v',
            ],
          },
          ag08jtrpa26j: {
            sku: '6239',
            project: '59',
            relevantVariants: [
              '2026-premium-luxury',
            ],
          },
          r7nn5ntbszly: {
            sku: '6231',
            project: '59',
            relevantVariants: [
              '2026-premium-luxury',
            ],
          },
          ojc9ptszsqvf: {
            sku: '6229',
            project: '59',
            relevantVariants: [
              '2026-premium-luxury',
            ],
          },
          ywn6ec8h269z: {
            sku: '6230',
            project: '59',
            relevantVariants: [
              '2026-premium-luxury',
            ],
          },
          x64x0w8ca6xy: {
            sku: '6236',
            project: '59',
            relevantVariants: [
              '2026-premium-luxury',
            ],
          },
          esnofdor9647: {
            sku: '6234',
            project: '59',
            relevantVariants: [
              '2026-premium-luxury',
            ],
          },
          '2fa06p9t7v8f': {
            sku: '6239',
            project: '59',
            relevantVariants: [
              '2026-luxury',
            ],
          },
          '69xux2hlscm1': {
            sku: '6231',
            project: '59',
            relevantVariants: [
              '2026-luxury',
            ],
          },
          '5xzh26yrdinw': {
            sku: '6229',
            project: '59',
            relevantVariants: [
              '2026-luxury',
            ],
          },
          '2fbmj48vpgsi': {
            sku: '6230',
            project: '59',
            relevantVariants: [
              '2026-luxury',
            ],
          },
          '2fbmj4ff8vpgsi': {
            sku: '6236',
            project: '59',
            relevantVariants: [
              '2026-luxury',
            ],
          },
          '2fbmffj4ff8vpgsi': {
            sku: '6234',
            project: '59',
            relevantVariants: [
              '2026-luxury',
            ],
          },
        },
        interior: {
          '86cgiceo3f7n': {
            sku: '5021',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          '48za413x82621': {
            sku: '5023',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          '48za413x82622': {
            sku: '5022',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          i8gu23g9uev7: {
            sku: '5024',
            project: '59',
            relevantVariants: [
              '2025-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          j1x49yhmrfs1: {
            sku: '6232',
            project: '59',
            relevantVariants: [
              '2026-optiq-v',
            ],
            notCompatibleExteriorColors: [],
          },
          v4qpw0s0id1s: {
            sku: '6237',
            project: '59',
            relevantVariants: [
              '2026-optiq-v',
            ],
            notCompatibleExteriorColors: [],
          },
          zgaapk0bjy1c: {
            sku: '6371',
            project: '59',
            relevantVariants: [
              '2026-premium-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          mip65q6evc31: {
            sku: '6373',
            project: '59',
            relevantVariants: [
              '2026-premium-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          dcfdkz0j4nst: {
            sku: '6375',
            project: '59',
            relevantVariants: [
              '2026-premium-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          yzm9eaoguz90: {
            sku: '6374',
            project: '59',
            relevantVariants: [
              '2026-premium-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          '48zalpo67x82621': {
            sku: '6331',
            project: '59',
            relevantVariants: [
              '2026-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          '48zalpo77x82621': {
            sku: '6372',
            project: '59',
            relevantVariants: [
              '2026-luxury',
            ],
            notCompatibleExteriorColors: [],
          },
        },
      },
      features: {
        dm0m2aj1jvy3: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Exterior Anim Lights',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Exterior Anim Lights',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1cuzidmw9n3g': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Front Side View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Front Side View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3siptpq2iqi9': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        lljiom38dn2i: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Rear Quarter View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Rear Quarter View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        mpm1i9izpcfc: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '0kweybqq8kf3': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Interior view',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Interior view',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        yygvqpfvv5rt: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        kxtve76vtocg: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'Middle Row Legroom',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ejzw6sqj44xd: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Info Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Info Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '4w2u45bmimkf': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2025-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Speakers',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speakers',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        zdesiualecwi: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'Front Side View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Front Side View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        wi021bet8rkn: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'SHADER_Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        jleviy76yfqe: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'SHADER_Rear Quarter View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Rear Quarter View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        mn8oi8vwsr1l: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'SHADER_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        bzmmdartkj4j21: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'SHADER_Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '9mkaqqs8ersr': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'SHADER_Interior view',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Interior view',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        s2ezeadsngz7: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        qasbrtm6gfyy47: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'SHADER_Signature Seating',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Signature Seating',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        qaopbrtm6gfyy47: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'SHADER_Steering',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Steering',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        h2fv1pe6q2dc: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'Middle Row Legroom',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        rglk1xge652a: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'SHADER_Info Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Info_Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        bmrgvh33m9hw: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-optiq-v',
          ],
          messages: [
            {
              message: 'SHADER_Speakers',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speakers',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        qxg0y3opafre: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-premium-luxury',
          ],
          messages: [
            {
              message: 'Exterior Anim Lights',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Exterior Anim Lights',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        q78piidva3ud: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-premium-luxury',
          ],
          messages: [
            {
              message: 'Front Side View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Front Side View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        p852y9tfv7t9: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-premium-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3ofh8yzwbhdc': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-premium-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Rear Quarter View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Rear Quarter View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        eogysgpcrofp: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-premium-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        sfk75k7d0m4s: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-premium-luxury',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        zz2trf9exnm0: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-premium-luxury',
          ],
          messages: [
            {
              message: 'Middle Row Legroom',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        u6tufvfiuhhz: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-premium-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Info Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Info_Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        v4i019a0k6qk: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-premium-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Speakers',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speakers',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '29kx4ja7eg7r': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-luxury',
          ],
          messages: [
            {
              message: 'Exterior Anim Lights',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Exterior Anim Lights',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3ud49gqncb2e': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-luxury',
          ],
          messages: [
            {
              message: 'Front Side View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Front Side View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        qcwd61vh2s9y: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        pcwqd3petb29: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Rear Quarter View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Rear Quarter View',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        bzmmdakj4j21: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            '2026-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        qasbm6gfyy47: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-luxury',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        gip7cgbu25uv: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-luxury',
          ],
          messages: [
            {
              message: 'Middle Row Legroom',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '77i0h4ejxjbt': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Info Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Info_Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1jhh1m7485kl': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            '2026-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Speakers',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speakers',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
      },
      accessories: {
        '3ckvx6gvve67': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Front Badge Illuminated',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_Front Badge Illuminated',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ACC_Front Badge',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '60cyi4386dh7': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'E_1_Door_OSRVM_Cap_S4X_FL',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_E_1_Door_OSRVM_Cap_S4X_FL',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ACC_OSRVM_Cap',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            xl0huhq8cgps: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '26awwpydj3k4': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Roof_CrossRails_VQQ',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_Roof_CrossRails_VQQ',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '41j05csmotu4': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Side_Mud_Guards',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_Side_Mud_Guards',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ACC_Side_Mud_Guards',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '4g3h4ls28grj': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Side_Perimeter_Lamps_S3I',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_Side_Perimeter_Lamps_S3I',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {
            xl0huhq8cgps: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '56cy0d0cnz7u': {
          view: 'interior',
          groupId: '2025-luxury-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Floor_Mat_VAV',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_Floor_Mat_VAV',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ACC_Floor_Mat_VAV',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '2esb5gdmfpk8': {
          view: 'interior',
          groupId: '2025-luxury-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Floor_Liner_RIA',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_Floor_Liner_RIA',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ACC_Floor_Liner_RIA',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '496aqzregvz5': {
          view: 'exterior',
          groupId: '2025-luxury-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Trunk_Cargo_BoxMat_SD3',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_Trunk_Cargo_BoxMat_SD3',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {
            hdmtouug3mrz: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '4e8rsvm8yulq': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Trunk_Cargo_Net_W2D',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_Trunk_Cargo_Net_W2D',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {
            hdmtouug3mrz: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '6bmwunaxygjc': {
          view: 'exterior',
          groupId: '2025-luxury-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Trunk_Cargo_Organiser_RWU',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_Trunk_Cargo_Organiser_RWU',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {
            hdmtouug3mrz: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '3l2bs2aiit7y': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Trunk_Cargo_Shade_VRS',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_Trunk_Cargo_Shade_VRS',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ACC_Trunk_Cargo_Shade',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            hdmtouug3mrz: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '50t7txtk3kvs': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'I_7_Trunk_Cargo_Tray_CAV',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_I_7_Trunk_Cargo_Tray_CAV',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ACC_Trunk_Cargo_Tray_CAV',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            hdmtouug3mrz: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '3ktjpvxvvvkj': {
          view: 'exterior',
          groupId: '2025-luxury-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'I_7_Trunk_Cargo_Tray_SHX',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Accessory_I_7_Trunk_Cargo_Tray_SHX',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ACC_Trunk_Cargo_Tray_SHX',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            hdmtouug3mrz: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2025-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        z7ne7fg1ds4c: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variants parts_VSERIES',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        g3ieipszphfu: {
          view: 'exterior',
          groupId: '2026-optiq-v-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Santorini_Blue_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        a9hj0iqo1mct: {
          view: 'exterior',
          groupId: '2026-optiq-v-g0',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Santorini_Blue_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        aanz6fiqy06r: {
          view: 'exterior',
          groupId: '2026-optiq-v-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Carbon_Fiber_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        cii7k7g4ittk: {
          view: 'exterior',
          groupId: '2026-optiq-v-g1',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Carbon_Fiber_Package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5ckoklookvx6gpve78': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Carpet_V_Series_Logo',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5cklopookvx6gpve78': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Black_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5ckloplopjx6gpve78': {
          view: 'exterior',
          groupId: '2026-optiq-v-g2',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Alloy_Optional_Vseries',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5ckmnjx6gpve78': {
          view: 'exterior',
          groupId: '2026-optiq-v-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Alloy_Default_Vseries',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ALLOY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5ckmnjxlo6gpve78': {
          view: 'exterior',
          groupId: '2026-optiq-v-g3',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Alloy_Calipers_Default_VSeries',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5ckmrrxlo6gpve78': {
          view: 'exterior',
          groupId: '2026-optiq-v-g3',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Alloy_Calipers_Optional_VSeries',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_CALIPER',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_CALIPER',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5cksewrxlo6gpve78': {
          view: 'interior',
          groupId: '2026-optiq-v-g4',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Carpet_Default',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5cksepolo6gpve78': {
          view: 'interior',
          groupId: '2026-optiq-v-g4',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Carpet_Optional',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_MATS',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_MATS',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-optiq-v',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '2ud18g1zcqja': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variants parts_Pre_Lux',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-premium-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '4r9ujh1ms88e': {
          view: 'interior',
          groupId: '2026-premium-luxury-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Carpet_Default',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-premium-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5ckokvx6gvve78': {
          view: 'interior',
          groupId: '2026-premium-luxury-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Carpet_Optional',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_MATS',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_MATS',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-premium-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        bdbqb1zg1dr5: {
          view: 'exterior',
          groupId: '2026-premium-luxury-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Colorbody_Roof',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-premium-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ti5n7zx2wp04: {
          view: 'exterior',
          groupId: '2026-premium-luxury-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Black_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-premium-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '3ckvx6gvve78': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variants parts_Lux',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '3ckokvx6gvve78': {
          view: 'interior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Carpet_Default',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5ckokokvx6gvve78': {
          view: 'exterior',
          groupId: '2026-luxury-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Colorbody_Roof',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5ckokokvx6gpve78': {
          view: 'exterior',
          groupId: '2026-luxury-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Black_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            '2026-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
      },
      packages: {
        x7vtyriufcgr0: {
          groupId: '2026-optiq-v-pkg0',
          accessoryIds: [
            'a9hj0iqo1mct',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            '2026-optiq-v',
          ],
        },
        x7vtyroklpiufcgr0: {
          groupId: '2026-optiq-v-pkg1',
          accessoryIds: [
            'cii7k7g4ittk',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            '2026-optiq-v',
          ],
        },
      },
      interactions: {
        xl0huhq8cgps: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        hdmtouug3mrz: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        '2mbv5mgd2j0t': {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_OPEN',
              identifiers: [
                'common_interior',
                'common_exterior',
                'exterior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_CLOSE',
              identifiers: [
                'common_interior',
                'common_exterior',
                'exterior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 3.2,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        dp3auttujwro: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        togk4c21fw9i: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        eylu0s3u2euf: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        c2wy0i8hrovk: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_SHADE_OPEN',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_SHADE_CLOSE',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7007,
                beta: 0.232,
                radius: 4,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.704,
                beta: 2.489,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.73,
                beta: 2.3,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7,
                beta: 2.01,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            '2025-luxury',
          ],
        },
        '2r1ytw5hxg6p': {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-optiq-v',
          ],
        },
        '6dvsse8x5znz': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-optiq-v',
          ],
        },
        vt6cdmgxpezd: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-optiq-v',
          ],
        },
        nnc9ej7gplbx: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-optiq-v',
          ],
        },
        '4fgs1ckkh5ji': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-optiq-v',
          ],
        },
        j9ejmhggh9r0: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_SHADE_OPEN',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_SHADE_CLOSE',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7007,
                beta: 0.232,
                radius: 4,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.704,
                beta: 2.489,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.73,
                beta: 2.3,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7,
                beta: 2.01,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            '2026-optiq-v',
          ],
        },
        '5fw1njuowv83': {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-premium-luxury',
          ],
        },
        qw15zz0wqp7s: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-premium-luxury',
          ],
        },
        atbbyogclf16: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-premium-luxury',
          ],
        },
        s3k5pi374ml2: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-premium-luxury',
          ],
        },
        '3hmjpixzot2a': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-premium-luxury',
          ],
        },
        onp9lnl4hjta: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_SHADE_OPEN',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_SHADE_CLOSE',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7007,
                beta: 0.232,
                radius: 4,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.704,
                beta: 2.489,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.73,
                beta: 2.3,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7,
                beta: 2.01,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            '2026-premium-luxury',
          ],
        },
        f9s7b4hdeiz7: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-luxury',
          ],
        },
        '1nad76ictp6j': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-luxury',
          ],
        },
        '3cqy8q29b96c': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-luxury',
          ],
        },
        '2ca59s6yd3u9': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-luxury',
          ],
        },
        '1dzdmvhl8vp3': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            '2026-luxury',
          ],
        },
        mAlX4SRB_per: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_SHADE_OPEN',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_SHADE_CLOSE',
              identifiers: [
                'common_interior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7007,
                beta: 0.232,
                radius: 4,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.704,
                beta: 2.489,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.73,
                beta: 2.3,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7,
                beta: 2.01,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            '2026-luxury',
          ],
        },
      },
      hotspots: {
        exteriorCamera: {
          type: 'exterior',
          messages: {
            fov: 0.8,
            alpha: 2.02,
            beta: 1.47,
            radius: 5.7,
            lowerAlphaLimit: null,
            upperAlphaLimit: null,
            lowerBetaLimit: 0.1,
            upperBetaLimit: 1.6,
            lowerRadiusLimit: 3.2,
            upperRadiusLimit: 6.7,
            target: {
              x: 0,
              y: 0.65,
              z: 0,
            },
            isFovZoomModeOn: false,
          },
        },
        interiorCamera: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_1: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_2',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_2: {
          type: 'interior',
          messages: {},
        },
      },
      usdzPathWithVariable: '',
    },
  },
};
